import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, MapPin, Check, ArrowLeft, Phone, Mail, Calendar, Users, Utensils, Copy, MessageCircle, Hash, Info, Image as ImageIcon, Video as VideoIcon, Bed, BedDouble, MessageSquare, HelpCircle, Share2, Tag, X, BadgeCheck } from "lucide-react";
import PageTransition from "../components/PageTransition";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import HotelReviews from "../components/HotelReviews";
import FAQSection from "../components/FAQSection";
import SocialStrip from "../components/SocialStrip";
import ExtraContacts from "../components/ExtraContacts";
import Lightbox from "../components/Lightbox";
import DateRangePicker from "../components/DateRangePicker";
import GuestSelector from "../components/GuestSelector";
import FlexibleDates from "../components/FlexibleDates";
import { resolveAmenities } from "../lib/amenities";
import { resolveImageUrl } from "../lib/imageUrl";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { nightsBetween } from "../lib/dates";
import { nightlyRate, stayTotal } from "../lib/roomPricing";

const HotelDetail = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { t, settings, formatPrice, currency, lang } = useApp();
    const { customer } = useCustomerAuth();
    const isCompany = customer?.account_type === "company";
    const [showCompanyGate, setShowCompanyGate] = useState(false);
    const [hotel, setHotel] = useState(null);
    const [activeRoomId, setActiveRoomId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [paying, setPaying] = useState(null);
    const [tab, setTab] = useState("overview");
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [promoInput, setPromoInput] = useState("");
    const [promo, setPromo] = useState(null);
    const [promoLoading, setPromoLoading] = useState(false);

    const today = new Date().toISOString().split("T")[0];
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

    const initRooms = Number(searchParams.get("rooms")) || 1;
    const [form, setForm] = useState({
        customer_type: "individual",
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        company_name: "",
        check_in: searchParams.get("check_in") || today,
        check_out: searchParams.get("check_out") || tomorrow,
        adults: Number(searchParams.get("adults")) || Number(searchParams.get("guests")) || 2,
        children: Number(searchParams.get("children")) || 0,
        infants: Number(searchParams.get("infants")) || 0,
        rooms_count: initRooms,
        breakdown: {
            double: searchParams.get("bd_double") != null ? Number(searchParams.get("bd_double")) || 0 : initRooms,
            triple: Number(searchParams.get("bd_triple")) || 0,
            quad: Number(searchParams.get("bd_quad")) || 0,
        },
        meal_plan: "none",
        notes: "",
    });

    // Auto-fill from logged-in customer (still editable); company accounts get company pricing automatically
    useEffect(() => {
        if (!customer) {
            setForm((f) => (f.customer_type === "individual" ? f : { ...f, customer_type: "individual" }));
            return;
        }
        setForm((f) => ({
            ...f,
            customer_name: f.customer_name || customer.name || "",
            customer_phone: f.customer_phone || customer.phone || "",
            customer_email: f.customer_email || customer.email || "",
            customer_type: customer.account_type === "company" ? "tour" : "individual",
            company_name: f.company_name || customer.company_name || "",
        }));
    }, [customer]);

    useEffect(() => {
        api.get(`/hotels/${id}`)
            .then(({ data }) => {
                setHotel(data);
                if (data.rooms?.length) setActiveRoomId(data.rooms[0].id);
            })
            .catch(() => toast.error("Otel bulunamadı"));
    }, [id]);

    const activeRoom = useMemo(() => hotel?.rooms?.find((r) => r.id === activeRoomId), [hotel, activeRoomId]);

    // Per-day room price for the calendar (Booking.com style)
    const dayPrice = React.useCallback((date) => {
        if (!activeRoom) return null;
        const val = nightlyRate(activeRoom, date, isCompany);
        return val > 0 ? formatPrice(val, { showSymbol: false }) : null;
    }, [activeRoom, isCompany, formatPrice]);

    const nights = useMemo(() => {
        try {
            const a = new Date(form.check_in);
            const b = new Date(form.check_out);
            return Math.max(1, Math.round((b - a) / 86400000));
        } catch { return 1; }
    }, [form.check_in, form.check_out]);

    const mealOptions = useMemo(() => {
        if (!activeRoom) return [];
        // Prefer dynamic meal_plans from admin; fall back to legacy 3-fixed plans only if they have non-zero price.
        const dyn = (activeRoom.meal_plans || []).map((p) => ({ id: p.id, label: p.label, price: Number(p.price) || 0 }));
        if (dyn.length > 0) return dyn;
        const legacy = [];
        if (Number(activeRoom.meal_breakfast) > 0) legacy.push({ id: "breakfast", label: "Kahvaltı", price: Number(activeRoom.meal_breakfast) });
        if (Number(activeRoom.meal_half_board) > 0) legacy.push({ id: "half_board", label: "Yarım Pansiyon", price: Number(activeRoom.meal_half_board) });
        if (Number(activeRoom.meal_full_board) > 0) legacy.push({ id: "full_board", label: "Tam Pansiyon", price: Number(activeRoom.meal_full_board) });
        return legacy;
    }, [activeRoom]);

    const bedGuests = form.adults + form.children; // infants: no bed

    // Room distribution (2/3/4-person) → matched to hotel room types by capacity; stay-aware pricing (range > month > base)
    const breakdownLines = useMemo(() => {
        if (!hotel) return [];
        const isBulk = form.customer_type === "tour";
        return [["double", 2], ["triple", 3], ["quad", 4]].map(([key, cap]) => {
            const count = Number(form.breakdown?.[key]) || 0;
            if (count <= 0) return null;
            const r = hotel.rooms?.find((x) => Number(x.capacity) === cap) || activeRoom;
            if (!r) return null;
            const stay = stayTotal(r, form.check_in, nights, isBulk);
            return { key, cap, count, room: r, perNight: nights > 0 ? stay / nights : 0, stay };
        }).filter(Boolean);
    }, [hotel, form.breakdown, form.customer_type, form.check_in, nights, activeRoom]);
    const breakdownSum = (Number(form.breakdown?.double) || 0) + (Number(form.breakdown?.triple) || 0) + (Number(form.breakdown?.quad) || 0);
    const breakdownValid = breakdownSum === form.rooms_count && breakdownSum > 0;

    const pricing = useMemo(() => {
        if (!activeRoom) return { room: 0, meal: 0, noMealDiscount: 0, promoDiscount: 0, total: 0, deposit: 0, perNight: 0, nightlyNoMeal: 0 };
        const room = breakdownLines.length > 0
            ? breakdownLines.reduce((s, l) => s + l.stay * l.count, 0)
            : stayTotal(activeRoom, form.check_in, nights, form.customer_type === "tour") * form.rooms_count;
        const perNight = nights > 0 ? room / nights : 0;
        const selected = mealOptions.find((m) => m.id === form.meal_plan);
        const mealPer = selected ? selected.price : 0;
        // Meals are charged for adults only — children (2-9) & infants (<2) are meal-free
        const meal = mealPer * form.adults * nights;
        const noMealPer = Number(settings.no_meal_discount) || 0;
        const noMealDiscount = form.meal_plan === "none" && noMealPer > 0 ? noMealPer * form.adults * nights : 0;
        const baseTotal = Math.max(0, room + meal - noMealDiscount);
        let promoDiscount = 0;
        if (promo) {
            promoDiscount = promo.discount_type === "fixed"
                ? Math.min(baseTotal, Number(promo.discount_value) || 0)
                : baseTotal * (Number(promo.discount_value) || 0) / 100;
            promoDiscount = Math.round(promoDiscount * 100) / 100;
        }
        const total = Math.max(0, baseTotal - promoDiscount);
        const deposit = (total * (settings.deposit_percent || 10)) / 100;
        // Nightly no-meal (all rooms) = per-night total minus per-person no-meal discount (visual hint)
        const nightlyNoMeal = Math.max(0, perNight - noMealPer * form.adults);
        return { room, meal, noMealDiscount, promoDiscount, total, deposit, perNight, nightlyNoMeal };
    }, [activeRoom, form, nights, settings.deposit_percent, settings.no_meal_discount, mealOptions, promo, breakdownLines]);

    const applyPromo = async () => {
        const code = promoInput.trim().toUpperCase();
        if (!code) return;
        setPromoLoading(true);
        try {
            const { data } = await api.post("/promo/validate", { code });
            setPromo(data);
            toast.success(`İndirim kodu uygulandı: ${data.code}`);
        } catch (err) {
            setPromo(null);
            toast.error(err.response?.data?.detail || "Kod geçersiz");
        } finally { setPromoLoading(false); }
    };

    // Search analytics: log date searches on this hotel (debounced, skip initial)
    const searchLoggedRef = React.useRef(null);
    useEffect(() => {
        const key = `${form.check_in}|${form.check_out}|${bedGuests}`;
        if (searchLoggedRef.current === null) { searchLoggedRef.current = key; return; }
        if (searchLoggedRef.current === key) return;
        const timer = setTimeout(() => {
            searchLoggedRef.current = key;
            api.post("/analytics/search", {
                check_in: form.check_in, check_out: form.check_out, guests: bedGuests,
                customer_type: form.customer_type, city: hotel?.city || null, source: "hotel_detail",
            }).catch(() => {});
        }, 1200);
        return () => clearTimeout(timer);
    }, [form.check_in, form.check_out, bedGuests, form.customer_type, hotel]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!activeRoom) return;
        if (!form.customer_name || !form.customer_phone) { toast.error("Ad ve telefon zorunlu"); return; }
        if (!breakdownValid) {
            toast.error(breakdownSum < form.rooms_count
                ? `Lütfen oda dağılımını tamamlayın — ${form.rooms_count - breakdownSum} oda daha dağıtılmalı (2/3/4 kişilik).`
                : `Oda dağılımı toplam oda sayısını aşıyor. Lütfen "Misafir & Oda" alanından düzeltin.`);
            return;
        }
        setSubmitting(true);
        try {
            const primaryRoomId = breakdownLines[0]?.room?.id || activeRoom.id;
            const payload = { hotel_id: hotel.id, room_id: primaryRoomId, ...form, guests: bedGuests, room_breakdown: form.breakdown };
            delete payload.breakdown;
            if (promo) payload.promo_code = promo.code;
            if (!payload.customer_email) delete payload.customer_email;
            if (!payload.company_name) delete payload.company_name;
            if (!payload.notes) delete payload.notes;
            const { data } = await api.post("/reservations", payload);
            setResult(data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join(" · ")
                : typeof detail === "string" ? detail : "Rezervasyon gönderilemedi";
            toast.error(msg);
        } finally { setSubmitting(false); }
    };

    const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success("Kod kopyalandı"); };

    if (!hotel) {
        return <PageTransition><div className="max-w-7xl mx-auto p-10 text-muted_green">Yükleniyor…</div></PageTransition>;
    }

    if (result) {
        return (
            <PageTransition>
                <div className="max-w-3xl mx-auto px-6 py-20">
                    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }} className="border border-gold bg-emerald_surface p-10 lg:p-14 text-center" data-testid="reservation-success">
                        <div className="ornament-divider mb-6"><span className="font-arabic text-3xl text-gold">۞</span></div>
                        <h1 className="font-serif text-4xl lg:text-5xl mb-3">Rezervasyon Alındı</h1>
                        <p className="text-muted_green mb-8">Talebiniz başarıyla iletildi. Lütfen rezervasyon numaranızı kaydedin.</p>
                        <div className="inline-block border border-gold/40 bg-emerald_deep/60 px-8 py-6 mb-8">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted_green mb-2 flex items-center justify-center gap-1">
                                <Hash className="w-3 h-3" /> Rezervasyon Numaranız
                            </div>
                            <div className="font-mono text-3xl lg:text-4xl text-gold tracking-[0.2em]" data-testid="reservation-code">
                                {result.reservation_code}
                            </div>
                            <button onClick={() => copyCode(result.reservation_code)} className="mt-3 text-[10px] uppercase tracking-[0.2em] text-muted_green hover:text-gold inline-flex items-center gap-1">
                                <Copy className="w-3 h-3" /> Kopyala
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-left text-sm border-t border-border_green pt-6 mb-8 max-w-md mx-auto">
                            <div><span className="text-muted_green">Otel:</span><div>{result.hotel_name}</div></div>
                            <div><span className="text-muted_green">Oda:</span><div>{result.room_name}</div></div>
                            <div><span className="text-muted_green">Toplam:</span><div className="text-gold font-serif text-lg">{formatPrice(result.total_price)}</div></div>
                            <div><span className="text-muted_green">%{settings.deposit_percent || 10} Depozito:</span><div className="text-gold font-serif text-lg">{formatPrice(result.deposit_amount)}</div></div>
                            {Number(result.promo_discount) > 0 && (
                                <div className="col-span-2 border-t border-border_green pt-2" data-testid="result-promo-line">
                                    <span className="text-muted_green">İndirim Kodu:</span>{" "}
                                    <span className="font-mono text-emerald-300">{result.promo_code}</span>{" "}
                                    <span className="text-emerald-300">(−{formatPrice(result.promo_discount)})</span>
                                </div>
                            )}
                        </div>
                        <div className="bg-emerald_deep/50 border border-border_green p-5 mb-4 text-left text-sm">
                            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Online Ödeme (Stripe)</div>
                            <p className="text-muted_green text-xs mb-4 leading-relaxed">Kredi kartı ile güvenli ödeme yapabilirsiniz. İki seçenek sunuyoruz:</p>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {(() => {
                                    const full = Number(result.total_price) || 0;
                                    const dep = Number(result.deposit_amount) || 0;
                                    const surcharge = Math.round(full * 10) / 100 * 1;
                                    const fullTotal = Math.round((full + surcharge) * 100) / 100;
                                    const payWith = async (type) => {
                                        setPaying(type);
                                        try {
                                            const { data } = await api.post("/payments/create-session", {
                                                reservation_id: result.id,
                                                payment_type: type,
                                                origin_url: window.location.origin,
                                            });
                                            if (data.url) window.location.href = data.url;
                                            else throw new Error("Ödeme oturumu oluşturulamadı");
                                        } catch (err) {
                                            toast.error(err.response?.data?.detail || "Ödeme başlatılamadı");
                                            setPaying(null);
                                        }
                                    };
                                    return (
                                        <>
                                            <button
                                                onClick={() => payWith("deposit")}
                                                disabled={!!paying}
                                                className="border border-gold/60 bg-emerald_deep/40 hover:bg-emerald_hover p-4 text-left transition-colors disabled:opacity-50"
                                                data-testid="pay-deposit-btn"
                                            >
                                                <div className="text-[10px] uppercase tracking-[0.15em] text-gold mb-1">{`%${settings.deposit_percent || 10} Depozit`}</div>
                                                <div className="font-mono text-xl text-bone">{formatPrice(dep)}</div>
                                                <div className="text-[11px] text-muted_green mt-1">Kalanı otelde ödersiniz</div>
                                                {paying === "deposit" && <div className="text-[10px] text-gold mt-2">Yönlendiriliyor…</div>}
                                            </button>
                                            <button
                                                onClick={() => payWith("full")}
                                                disabled={!!paying}
                                                className="border border-emerald-500/60 bg-emerald_deep/40 hover:bg-emerald_hover p-4 text-left transition-colors disabled:opacity-50"
                                                data-testid="pay-full-btn"
                                            >
                                                <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-300 mb-1">Tam Ödeme <span className="ml-1 text-rose-300">+ %10 online farkı</span></div>
                                                <div className="font-mono text-xl text-bone">{formatPrice(fullTotal)}</div>
                                                <div className="text-[11px] text-muted_green mt-1">Otelde ek ödeme yok</div>
                                                {paying === "full" && <div className="text-[10px] text-gold mt-2">Yönlendiriliyor…</div>}
                                            </button>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>

                        <div className="bg-emerald_deep/50 border border-border_green p-5 mb-8 text-left text-sm">
                            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">IBAN Ön Ödeme Bilgileri</div>
                            <div className="space-y-1.5 text-bone/80">
                                <div><span className="text-muted_green">Banka:</span> {settings.bank_name}</div>
                                <div><span className="text-muted_green">IBAN:</span> <span className="font-mono">{settings.iban}</span></div>
                                <div><span className="text-muted_green">Hesap Sahibi:</span> {settings.account_holder}</div>
                                <div className="text-muted_green text-xs mt-2 pt-2 border-t border-border_green">
                                    Açıklama olarak <span className="text-gold font-mono">{result.reservation_code}</span> yazmayı unutmayın.
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-4">
                            {result.whatsapp_url && (
                                <a href={result.whatsapp_url} target="_blank" rel="noreferrer" className="btn-gold" data-testid="whatsapp-confirm-btn">
                                    <MessageCircle className="w-4 h-4" /> WhatsApp ile Onayla
                                </a>
                            )}
                            <Link to="/track" className="btn-outline-gold">Durumu Takip Et</Link>
                        </div>
                    </motion.div>
                </div>
            </PageTransition>
        );
    }

    const isEmbeddableMap = (u) => !!u && (u.includes("output=embed") || u.includes("/maps/embed"));
    const mapSrc = (isEmbeddableMap(hotel.map_url) ? hotel.map_url : null) || (hotel.latitude && hotel.longitude
        ? `https://maps.google.com/maps?q=${hotel.latitude},${hotel.longitude}&hl=tr&z=16&output=embed`
        : hotel.address ? `https://maps.google.com/maps?q=${encodeURIComponent(hotel.address)}&hl=tr&z=15&output=embed` : null);

    const tabs = [
        { id: "overview", label: "Genel Bakış", icon: Info },
        { id: "photos", label: `Fotoğraflar (${hotel.images?.length || 0})`, icon: ImageIcon },
        ...(hotel.video_url ? [{ id: "video", label: "Tanıtım Videosu", icon: VideoIcon }] : []),
        { id: "location", label: "Konum", icon: MapPin },
        { id: "rooms", label: "Oda Tipleri", icon: Bed },
        { id: "reviews", label: "Yorumlar", icon: MessageSquare },
        { id: "social", label: "İletişim & Sosyal", icon: Share2 },
        { id: "faq", label: "SSS", icon: HelpCircle },
    ];

    return (
        <PageTransition>
            {/* COMPACT HERO */}
            <div className="relative h-[30vh] min-h-[220px] overflow-hidden">
                <motion.img
                    initial={{ scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: "easeOut" }}
                    src={resolveImageUrl(hotel.images?.[0])} alt={hotel.name}
                    className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
                    onClick={() => hotel.images?.length && setLightboxIndex(0)}
                    data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/50 to-black/85 pointer-events-none" />
                {hotel.images?.length > 1 && (
                    <button
                        onClick={() => setLightboxIndex(0)}
                        className="absolute top-4 right-4 z-10 bg-emerald_deep/85 hover:bg-gold hover:text-emerald_deep backdrop-blur text-bone text-[11px] uppercase tracking-[0.18em] font-semibold px-3 py-2 border border-gold/40 inline-flex items-center gap-2 transition-colors"
                        data-testid="open-gallery-btn"
                    >
                        <ImageIcon className="w-3.5 h-3.5" /> Tüm fotoğraflar ({hotel.images.length})
                    </button>
                )}
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-10">
                    <Link to="/" className="text-xs uppercase tracking-[0.2em] text-white/85 hover:text-gold inline-flex items-center gap-2 mb-4 w-fit">
                        <ArrowLeft className="w-3 h-3" /> Geri
                    </Link>
                    <div className="flex items-center gap-2 mb-2">
                        {Array.from({ length: hotel.star_rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}
                    </div>
                    <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="font-serif text-4xl lg:text-5xl text-white">
                        {hotel.name}
                    </motion.h1>
                    <div className="flex items-center gap-4 mt-2 text-white/80 text-sm flex-wrap">
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-gold" /> {hotel.city === "Mecca" ? "Mekke" : "Medine"}</span>
                        <span>·</span>
                        <span>{hotel.distance_to_haram} Harem'e mesafe</span>
                    </div>
                </div>
            </div>

            {/* TABS BAR */}
            <div className="sticky top-0 z-30 glass border-b border-border_green relative">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 flex overflow-x-auto no-scrollbar">
                    {tabs.map((tb) => {
                        const Icon = tb.icon;
                        return (
                            <button
                                key={tb.id}
                                onClick={() => {
                                    const el = document.getElementById(`section-${tb.id}`);
                                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                className={`px-4 sm:px-5 py-3.5 sm:py-4 text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] font-semibold border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 sm:gap-2 border-transparent text-bone/70 hover:text-gold hover:border-gold/40`}
                                data-testid={`tab-${tb.id}`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {tb.label}
                            </button>
                        );
                    })}
                </div>
                <div className="lg:hidden pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-emerald_deep/90 to-transparent" aria-hidden="true" />
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-3 gap-10 items-start">
                {/* LEFT: tab content */}
                <div className="lg:col-span-2">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={tab}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.3 }}
                        >
                            {(
                                <section id="section-overview" style={{ scrollMarginTop: 120 }}>
                                <div className="space-y-10">
                                    <div>
                                        <h2 className="font-serif text-3xl mb-4">Hakkında</h2>
                                        <p className="text-bone/80 leading-relaxed">{hotel.description}</p>
                                    </div>
                                    <div>
                                        <h2 className="font-serif text-3xl mb-5">Olanaklar</h2>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {resolveAmenities(hotel.amenities || []).map(({ icon: Icon, label }) => (
                                                <div key={label} className="flex items-center gap-2.5 text-sm border border-border_green bg-emerald_surface/40 px-3 py-2.5">
                                                    <Icon className="w-4 h-4 text-gold flex-shrink-0" />
                                                    <span>{label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Quick facts grid — fills the void on short hotels */}
                                    <div>
                                        <h2 className="font-serif text-3xl mb-5">Bu Otelde Sizi Bekleyenler</h2>
                                        <div className="grid sm:grid-cols-2 gap-3">
                                            <div className="border border-border_green bg-emerald_surface/40 p-4 flex items-start gap-3">
                                                <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Konum</div>
                                                    <div className="font-serif text-lg">{hotel.city === "Mecca" ? "Mekke" : "Medine"} · Harem'e {hotel.distance_to_haram}</div>
                                                </div>
                                            </div>
                                            <div className="border border-border_green bg-emerald_surface/40 p-4 flex items-start gap-3">
                                                <Star className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Sınıf</div>
                                                    <div className="font-serif text-lg">{hotel.star_rating || 5} Yıldız Otel</div>
                                                </div>
                                            </div>
                                            <div className="border border-border_green bg-emerald_surface/40 p-4 flex items-start gap-3">
                                                <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Ödeme</div>
                                                    <div className="font-serif text-lg">Güvenli Ödeme</div>
                                                    <div className="text-xs text-muted_green mt-0.5">Online veya banka havalesi</div>
                                                </div>
                                            </div>
                                            <div className="border border-border_green bg-emerald_surface/40 p-4 flex items-start gap-3">
                                                <MessageCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Onay Süreci</div>
                                                    <div className="font-serif text-lg">Anında WhatsApp Onayı</div>
                                                    <div className="text-xs text-muted_green mt-0.5">Genellikle dakikalar içinde</div>
                                                </div>
                                            </div>
                                            <div className="border border-border_green bg-emerald_surface/40 p-4 flex items-start gap-3 sm:col-span-2">
                                                <BedDouble className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Mevcut Oda Tipleri</div>
                                                    <div className="font-serif text-lg">{hotel.rooms?.length || 0} farklı oda seçeneği</div>
                                                    <div className="text-xs text-muted_green mt-0.5">
                                                        {(hotel.rooms || []).map((r) => r.name).join(" · ") || "Birden fazla seçenek mevcut"}
                                                    </div>
                                                </div>
                                                <button onClick={() => setTab("rooms")} className="text-[10px] uppercase tracking-[0.18em] text-gold hover:underline flex-shrink-0" data-testid="go-to-rooms-btn">
                                                    Görüntüle →
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>)}

                            {(
                                <section id="section-photos" style={{ scrollMarginTop: 120, marginTop: 40 }}>
                                <div>
                                    <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                                        <h2 className="font-serif text-3xl">Fotoğraf Galerisi</h2>
                                        {hotel.images?.length > 0 && (
                                            <span className="text-xs text-muted_green">
                                                {hotel.images.length} fotoğraf · büyütmek için tıklayın
                                            </span>
                                        )}
                                    </div>
                                    {hotel.images?.length ? (
                                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="photo-grid">
                                            {hotel.images.map((img, i) => (
                                                <button
                                                    key={img}
                                                    onClick={() => setLightboxIndex(i)}
                                                    className="relative block group overflow-hidden border border-border_green hover:border-gold transition-colors"
                                                    data-testid={`photo-thumb-${i}`}
                                                    aria-label={`Fotoğraf ${i + 1}`}
                                                >
                                                    <img
                                                        src={resolveImageUrl(img)}
                                                        alt={`${hotel.name} - ${i + 1}`}
                                                        loading="lazy"
                                                        className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-700"
                                                    />
                                                    <div className="absolute inset-0 bg-emerald_deep/0 group-hover:bg-emerald_deep/30 transition-colors flex items-center justify-center">
                                                        <span className="opacity-0 group-hover:opacity-100 text-[10px] uppercase tracking-[0.2em] text-bone font-semibold bg-emerald_deep/80 px-3 py-1.5 border border-gold/50 transition-opacity">
                                                            Büyüt
                                                        </span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-muted_green text-sm">Fotoğraf bulunamadı.</div>
                                    )}
                                </div>
                            </section>)}

                            {hotel.video_url && (
                                <section id="section-video" style={{ scrollMarginTop: 120, marginTop: 40 }}>
                                <div>
                                    <h2 className="font-serif text-3xl mb-6">Otel Tanıtım Videosu</h2>
                                    <video
                                        src={resolveImageUrl(hotel.video_url)}
                                        controls
                                        playsInline
                                        preload="metadata"
                                        className="w-full h-auto"
                                        data-testid="hotel-video-player"
                                    />
                                </div>
                            </section>)}

                            {(
                                <section id="section-location" style={{ scrollMarginTop: 120, marginTop: 40 }}>
                                <div>
                                    <h2 className="font-serif text-3xl mb-4">Konum</h2>
                                    {hotel.address && (
                                        <p className="text-sm text-muted_green mb-4 flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-gold" /> {hotel.address}
                                        </p>
                                    )}
                                    {mapSrc ? (
                                        <div className="border border-border_green overflow-hidden">
                                            <iframe
                                                title={`${hotel.name} konum`}
                                                src={mapSrc}
                                                width="100%"
                                                height="440"
                                                style={{ border: 0 }}
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                                data-testid="hotel-map"
                                            />
                                        </div>
                                    ) : (
                                        <div className="text-muted_green text-sm">Konum bilgisi bulunamadı.</div>
                                    )}
                                </div>
                            </section>)}

                            {(
                                <section id="section-rooms" style={{ scrollMarginTop: 120, marginTop: 40 }}>
                                <div>
                                    <h2 className="font-serif text-3xl mb-6">Oda Tipleri</h2>
                                    <div className="space-y-3">
                                        {hotel.rooms?.map((r) => (
                                            <div
                                                key={r.id}
                                                className={`border p-5 transition-colors cursor-pointer ${
                                                    activeRoomId === r.id ? "border-gold bg-emerald_hover" : "border-border_green hover:border-gold/40"
                                                }`}
                                                onClick={() => {
                                                    setActiveRoomId(r.id);
                                                    const key = { 2: "double", 3: "triple", 4: "quad" }[Number(r.capacity)];
                                                    if (key) setForm((f) => ({ ...f, breakdown: { double: 0, triple: 0, quad: 0, [key]: f.rooms_count } }));
                                                }}
                                                data-testid={`room-option-${r.id}`}
                                            >
                                                <div className="flex items-start justify-between flex-wrap gap-3">
                                                    <div>
                                                        <div className="font-serif text-xl">{r.name}</div>
                                                        <div className="text-xs text-muted_green mt-1">Kapasite: {r.capacity} kişi</div>
                                                        {((r.meal_plans || []).length > 0 || r.meal_breakfast || r.meal_half_board || r.meal_full_board) ? (
                                                            <div className="text-xs text-muted_green mt-1">
                                                                {(r.meal_plans || []).length > 0
                                                                    ? (r.meal_plans || []).map((m) => `${m.label}: +${formatPrice(m.price)}`).join(" · ")
                                                                    : [
                                                                          Number(r.meal_breakfast) > 0 && `Kahvaltı: +${formatPrice(r.meal_breakfast)}`,
                                                                          Number(r.meal_half_board) > 0 && `Yarım: +${formatPrice(r.meal_half_board)}`,
                                                                          Number(r.meal_full_board) > 0 && `Tam: +${formatPrice(r.meal_full_board)}`,
                                                                      ].filter(Boolean).join(" · ")}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">Gece</div>
                                                        {(() => {
                                                            const base = isCompany && r.price_bulk != null ? r.price_bulk : r.price_individual;
                                                            return (
                                                                <>
                                                                    {Number(hotel.discount_percent) > 0 && (
                                                                        <div className="flex items-center justify-end gap-1.5" data-testid={`room-discount-${r.id}`}>
                                                                            <span className="line-through text-muted_green text-xs">{formatPrice(Math.round(base / (1 - Number(hotel.discount_percent) / 100)))}</span>
                                                                            <span className="text-white text-[9px] font-bold px-1 py-0.5" style={{ background: "#c0392b" }}>−%{Math.round(hotel.discount_percent)}</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="font-serif text-2xl text-gold">{formatPrice(base)}</div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </section>)}

                            {(
                                <section id="section-reviews" style={{ scrollMarginTop: 120, marginTop: 40 }}>
                                <HotelReviews hotelId={hotel.id} />
                            </section>)}

                            {(
                                <section id="section-social" style={{ scrollMarginTop: 120, marginTop: 40 }}>
                                <div>
                                    <h2 className="font-serif text-3xl mb-2">İletişim & Sosyal Medya</h2>
                                    <p className="text-muted_green text-sm mb-6">Bizimle istediğiniz kanaldan iletişime geçin, gruplarımıza katılarak kampanyaları kaçırmayın.</p>
                                    <div className="grid sm:grid-cols-2 gap-3 mb-8">
                                        {settings.whatsapp_number && (
                                            <a
                                                href={`https://wa.me/${settings.whatsapp_number}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="border border-border_green hover:border-gold p-5 bg-emerald_surface flex items-start gap-3 transition-colors"
                                                data-testid="hotel-contact-whatsapp"
                                            >
                                                <MessageCircle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">WhatsApp</div>
                                                    <div className="text-sm">+{settings.whatsapp_number}</div>
                                                </div>
                                            </a>
                                        )}
                                        {settings.contact_email && (
                                            <a
                                                href={`mailto:${settings.contact_email}`}
                                                className="border border-border_green hover:border-gold p-5 bg-emerald_surface flex items-start gap-3 transition-colors"
                                                data-testid="hotel-contact-email"
                                            >
                                                <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">E-posta</div>
                                                    <div className="text-sm break-all">{settings.contact_email}</div>
                                                </div>
                                            </a>
                                        )}
                                    </div>
                                    {(settings.extra_contacts || []).length > 0 && (
                                        <div className="border-t border-border_green pt-6 mb-6">
                                            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Ek İletişim Kanalları</div>
                                            <ExtraContacts items={settings.extra_contacts} compact />
                                        </div>
                                    )}
                                    <div className="border-t border-border_green pt-6">
                                        <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Bizi Takip Edin</div>
                                        <SocialStrip settings={settings} compact />
                                    </div>
                                </div>
                            </section>)}

                            {(
                                <section id="section-faq" style={{ scrollMarginTop: 120, marginTop: 40 }}>
                                <div>
                                    <h2 className="font-serif text-3xl mb-6">Sıkça Sorulan Sorular</h2>
                                    {(() => {
                                        const rawList = (hotel.faqs && hotel.faqs.length > 0) ? hotel.faqs : (settings.faqs || []);
                                        const faqList = rawList.map((f) => (lang === "en"
                                            ? { ...f, question: f.question_en || f.question, answer: f.answer_en || f.answer }
                                            : f));
                                        return faqList.length > 0 ? (
                                        <div className="space-y-2">
                                            {faqList.map((f) => (
                                                <details key={f.id} className="border border-border_green bg-emerald_surface group" data-testid={`hotel-faq-${f.id}`}>
                                                    <summary className="px-5 py-4 cursor-pointer flex items-center justify-between font-serif text-lg list-none">
                                                        <span>{f.question}</span>
                                                        <span className="text-gold text-xl group-open:rotate-45 transition-transform">+</span>
                                                    </summary>
                                                    <div className="px-5 pb-5 pt-1 text-sm text-muted_green border-t border-border_green leading-relaxed">{f.answer}</div>
                                                </details>
                                            ))}
                                        </div>
                                        ) : (
                                        <FAQSection title="" subtitle="" />
                                        );
                                    })()}
                                </div>
                            </section>)}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* RIGHT: Reservation (always visible on desktop, scrollable form) */}
                <aside className="min-w-0 order-first lg:order-none lg:sticky lg:top-4 self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pb-2">
                    <motion.form
                        onSubmit={handleSubmit}
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
                        className="border border-gold/40 bg-emerald_surface p-4 lg:p-5 space-y-3"
                    >
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <h3 className="font-serif text-xl">Hızlı Rezervasyon</h3>
                            <p className="text-[10px] text-muted_green">2 dakikada talebinizi gönderin</p>
                        </div>

                        {isCompany && (
                            <div className="border border-gold/40 bg-gold/10 px-3 py-2 text-[11px] text-gold flex items-center gap-2" data-testid="company-pricing-badge">
                                <BadgeCheck className="w-4 h-4 flex-shrink-0" />
                                Şirket hesabı — size özel toplu alım fiyatları uygulanıyor
                            </div>
                        )}

                        {form.customer_type === "tour" && (
                            <div>
                                <label className="label-dark">Şirket Adı</label>
                                <input type="text" className="input-dark" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="input-company" />
                            </div>
                        )}

                        <div>
                            <label className="label-dark">{lang === "en" ? "Stay dates" : "Konaklama Tarihi"}</label>
                            <div className="drp-in-form">
                                <DateRangePicker
                                    variant="dark"
                                    lang={lang}
                                    value={{ from: form.check_in, to: form.check_out }}
                                    onChange={({ from, to }) => setForm((f) => ({ ...f, check_in: from || f.check_in, check_out: to || f.check_out }))}
                                    testIds={{
                                        cellIn: "input-checkin-cell",
                                        cellOut: "input-checkout-cell",
                                        checkIn: "input-checkin",
                                        checkOut: "input-checkout",
                                    }}
                                    labels={{ checkIn: lang === "en" ? "Check-in" : "Giriş", checkOut: lang === "en" ? "Check-out" : "Çıkış" }}
                                    dayPrices={dayPrice}
                                />
                            </div>
                        </div>

                        <FlexibleDates
                            hotelId={hotel.id}
                            checkIn={form.check_in}
                            checkOut={form.check_out}
                            roomId={activeRoomId}
                            roomsCount={form.rooms_count}
                            adults={form.adults}
                            mealPlan={form.meal_plan}
                            customerType={form.customer_type}
                            onPick={({ check_in, check_out }) => setForm((f) => ({ ...f, check_in, check_out }))}
                            formatPrice={formatPrice}
                            lang={lang}
                        />

                        <div className="grid grid-cols-2 gap-2">
                            <div className="min-w-0">
                                <label className="label-dark">Misafir & Oda</label>
                                <GuestSelector
                                    variant="dark"
                                    lang={lang}
                                    showRooms
                                    showBreakdown
                                    value={{ adults: form.adults, children: form.children, infants: form.infants, rooms: form.rooms_count, breakdown: form.breakdown }}
                                    onChange={({ rooms, breakdown, ...g }) => setForm((f) => ({ ...f, ...g, rooms_count: rooms ?? f.rooms_count, breakdown: breakdown ?? f.breakdown }))}
                                    testId="booking-guests"
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="label-dark">Yemek</label>
                                <select value={form.meal_plan} onChange={(e) => setForm({ ...form, meal_plan: e.target.value })} className="input-dark" data-testid="select-meal">
                                    <option value="none">{`Yemek Yok`}</option>
                                    {mealOptions.map((m) => (
                                        <option key={m.id} value={m.id}>{`${m.label} (+${formatPrice(m.price)})`}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {!breakdownValid && (
                            <div className="text-[11px] text-amber-400" data-testid="breakdown-warning">
                                {breakdownSum < form.rooms_count
                                    ? `⚠ ${form.rooms_count - breakdownSum} oda daha dağıtılmalı — kaç adet 2/3/4 kişilik oda istediğinizi seçin.`
                                    : `⚠ Dağılım oda sayısını ${breakdownSum - form.rooms_count} aşıyor.`}
                            </div>
                        )}

                        <div>
                            <label className="label-dark">{lang === "en" ? "Room Distribution" : "Oda Dağılımı"}</label>
                            <div className="border border-border_green divide-y divide-border_green" data-testid="room-breakdown-summary">
                                {breakdownLines.length > 0 ? breakdownLines.map((l) => (
                                    <div key={l.key} className="flex items-center justify-between px-2.5 py-1.5 text-xs" data-testid={`breakdown-line-${l.key}`}>
                                        <span><span className="text-gold font-bold">{l.count}×</span> {l.room.name} <span className="text-[10px] text-muted_green">({l.cap} {lang === "en" ? "pax" : "kişilik"})</span></span>
                                        <span className="text-muted_green text-[11px]">{formatPrice(l.perNight)} / {lang === "en" ? "night" : "gece"}</span>
                                    </div>
                                )) : (
                                    <div className="px-2.5 py-1.5 text-xs text-amber-400">{lang === "en" ? "Select room distribution from the Guests & Rooms field" : "Misafir & Oda alanından oda dağılımını seçin"}</div>
                                )}
                            </div>
                        </div>

                        <div className="border-t border-border_green pt-3 grid grid-cols-2 gap-2">
                            <div className="min-w-0">
                                <label className="label-dark">Ad Soyad</label>
                                <input type="text" required className="input-dark" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="input-name" />
                            </div>
                            <div className="min-w-0">
                                <label className="label-dark"><Phone className="w-3 h-3 inline mr-1" /> WhatsApp</label>
                                <input type="tel" required className="input-dark" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+90 5xx xxx xx xx" data-testid="input-phone" />
                            </div>
                            {!customer && (
                                <div className="min-w-0">
                                    <label className="label-dark"><Mail className="w-3 h-3 inline mr-1" /> E-posta (ops.)</label>
                                    <input type="email" className="input-dark" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="input-email" />
                                </div>
                            )}
                            <div className="min-w-0">
                                <label className="label-dark"><Tag className="w-3 h-3 inline mr-1" /> İndirim Kodu</label>
                                {promo ? (
                                    <div className="flex items-center justify-between gap-1.5 border border-emerald-500/50 bg-emerald-900/25 px-2 py-2" data-testid="promo-applied-box">
                                        <span className="font-mono text-xs text-emerald-300 truncate">{promo.code}</span>
                                        <span className="text-[10px] text-emerald-300 whitespace-nowrap">
                                            {promo.discount_type === "percent" ? `%${promo.discount_value}` : `−${formatPrice(promo.discount_value)}`}
                                        </span>
                                        <button type="button" onClick={() => { setPromo(null); setPromoInput(""); }} className="text-muted_green hover:text-rose-400 flex-shrink-0" aria-label="Kodu kaldır" data-testid="remove-promo-btn">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex gap-1.5">
                                        <input
                                            type="text"
                                            className="input-dark flex-1 min-w-0 uppercase"
                                            value={promoInput}
                                            onChange={(e) => setPromoInput(e.target.value)}
                                            placeholder="Kod"
                                            data-testid="input-promo"
                                        />
                                        <button type="button" onClick={applyPromo} disabled={promoLoading || !promoInput.trim()} className="btn-outline-gold !text-[10px] !px-2.5 disabled:opacity-40 flex-shrink-0" data-testid="apply-promo-btn">
                                            {promoLoading ? "…" : "Uygula"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="border-t border-border_green pt-4 space-y-1 text-sm">
                            {(form.children > 0 || form.infants > 0) && (
                                <div className="text-[11px] text-muted_green pb-1" data-testid="guest-breakdown-line">
                                    {form.adults} yetişkin
                                    {form.children > 0 && <> · <span className="text-emerald-300">{form.children} çocuk (2-9 yaş, yemeksiz)</span></>}
                                    {form.infants > 0 && <> · <span className="text-emerald-300">{form.infants} bebek (yataksız & yemeksiz, ücretsiz)</span></>}
                                </div>
                            )}
                            <div className="flex justify-between text-muted_green text-xs">
                                <span>Gecelik yemeksiz (tüm odalar)</span>
                                <span data-testid="nightly-nomeal-price">{formatPrice(pricing.perNight, { decimals: Number.isInteger(pricing.perNight) ? 0 : 2 })} <span className="text-[10px] text-muted_green">/ gece</span></span>
                            </div>
                            <div className="flex justify-between text-muted_green text-xs">
                                <span>{nights} gece × {form.rooms_count} oda</span><span>{formatPrice(pricing.room)}</span>
                            </div>
                            {pricing.meal > 0 && (
                                <div className="flex justify-between text-muted_green text-xs">
                                    <span>Yemek</span><span>+ {formatPrice(pricing.meal)}</span>
                                </div>
                            )}
                            {pricing.noMealDiscount > 0 && (
                                <div className="flex justify-between text-emerald-300 text-xs" data-testid="no-meal-discount-line">
                                    <span>Yemeksiz indirimi</span><span>− {formatPrice(pricing.noMealDiscount)}</span>
                                </div>
                            )}
                            {pricing.promoDiscount > 0 && (
                                <div className="flex justify-between text-emerald-300 text-xs" data-testid="promo-discount-line">
                                    <span>İndirim kodu ({promo?.code})</span><span>− {formatPrice(pricing.promoDiscount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-serif text-xl text-gold pt-2 border-t border-border_green">
                                <span>Toplam</span><span data-testid="total-amount">{formatPrice(pricing.total)}</span>
                            </div>
                            <div className="text-[10px] text-muted_green mt-1">
                                <span className="text-gold">%{settings.deposit_percent || 10}</span> ön ödeme: <span className="text-gold">{formatPrice(pricing.deposit)}</span>
                                {currency !== "SAR" && <span> · {rates_source_label}</span>}
                            </div>
                        </div>

                        <button type="submit" disabled={submitting} className="btn-gold w-full !text-sm !py-3" data-testid="submit-reservation-btn">
                            {submitting ? "Gönderiliyor…" : "Talebi Gönder"}
                        </button>
                    </motion.form>
                </aside>
            </div>

            {/* Lightbox */}
            <Lightbox
                images={(hotel.images || []).map(resolveImageUrl)}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onChange={setLightboxIndex}
            />
        </PageTransition>
    );
};

const rates_source_label = "kur dönüştürüldü";

export default HotelDetail;
